import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './../glassPatternsDefs';

// Function to draw triangles with frames (similar to dN_Rect but for triangles)
export function shapeTriangle(svg, d, x, y, triangles, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};
  let innerPoints; // Declare outside forEach to make it accessible for return

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  triangles.forEach((triangle, index) => {
    const {
      w,
      h = prev.h ?? 100,
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,
      glassDesign = prev.glassDesign || null,
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      orientation = prev.orientation ?? 'right', // 'left' or 'right'
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel ?? true,
      middleFrame = prev.middleFrame ?? true  // Default true to show middle frame
    } = triangle;

    let baseFill = color;
    let tintOverlay = null;

    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white';
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }

    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign);
    }

    // Define triangle points based on orientation
    // For 'right': bottom-left, top-left, bottom-right (right angle at bottom-left, horizontal edge at bottom)
    // For 'left': bottom-right, top-right, bottom-left (right angle at bottom-right, horizontal edge at bottom)
    let outerPoints;

  if (orientation === 'center') {
  //
  // Center triangle: isosceles triangle pointing up
  // Base at bottom, apex at top center
  //
  const A = [currentX - frameWidth, y + h + frameWidth];        // bottom-left
  const B = [currentX + w / 2, y - frameWidth];                 // top-center (apex)
  const C = [currentX + w + frameWidth, y + h + frameWidth];    // bottom-right

  outerPoints = [A, B, C];

  //
  // Helper: offset a line segment inward by distance d
  //
  function offsetLine(P, Q, d) {
    const dx = Q[0] - P[0];
    const dy = Q[1] - P[1];
    const len = Math.hypot(dx, dy);

    // inward normal
    const nx = -dy / len;
    const ny = dx / len;

    return [
      [P[0] + nx * d, P[1] + ny * d],
      [Q[0] + nx * d, Q[1] + ny * d]
    ];
  }

  //
  // Helper: intersection of two infinite lines
  //
  function intersect(L1, L2) {
    const [A, B] = L1;
    const [C, D] = L2;

    const a1 = B[1] - A[1];
    const b1 = A[0] - B[0];
    const c1 = a1 * A[0] + b1 * A[1];

    const a2 = D[1] - C[1];
    const b2 = C[0] - D[0];
    const c2 = a2 * C[0] + b2 * C[1];

    const det = a1 * b2 - a2 * b1;

    return [
      (b2 * c1 - b1 * c2) / det,
      (a1 * c2 - a2 * c1) / det
    ];
  }

  //
  // Offset each edge inward by frameWidth
  //
  const AB = offsetLine(A, B, frameWidth);
  const BC = offsetLine(B, C, frameWidth);
  const CA = offsetLine(C, A, frameWidth);

  //
  // Intersect offset edges to get inner triangle
  //
  const A2 = intersect(AB, CA);
  const B2 = intersect(AB, BC);
  const C2 = intersect(BC, CA);

  innerPoints = [A2, B2, C2];
}
else if (orientation === 'right') {
  //
  // 1. Build the outer triangle (your original points)
  //
  const A = [currentX - frameWidth, y + h + frameWidth];   // bottom-left
  const B = [currentX - frameWidth, y - frameWidth];       // top-left
  const C = [currentX + w + frameWidth, y + h + frameWidth]; // bottom-right

  outerPoints = [A, B, C];

  //
  // 2. Helper: offset a line segment inward by distance d
  //
  function offsetLine(P, Q, d) {
    const dx = Q[0] - P[0];
    const dy = Q[1] - P[1];
    const len = Math.hypot(dx, dy);

    // inward normal
    const nx = -dy / len;
    const ny = dx / len;

    return [
      [P[0] + nx * d, P[1] + ny * d],
      [Q[0] + nx * d, Q[1] + ny * d]
    ];
  }

  //
  // 3. Helper: intersection of two infinite lines
  //
  function intersect(L1, L2) {
    const [A, B] = L1;
    const [C, D] = L2;

    const a1 = B[1] - A[1];
    const b1 = A[0] - B[0];
    const c1 = a1 * A[0] + b1 * A[1];

    const a2 = D[1] - C[1];
    const b2 = C[0] - D[0];
    const c2 = a2 * C[0] + b2 * C[1];

    const det = a1 * b2 - a2 * b1;

    return [
      (b2 * c1 - b1 * c2) / det,
      (a1 * c2 - a2 * c1) / det
    ];
  }

  //
  // 4. Offset each edge inward by frameWidth
  //
  const AB = offsetLine(A, B, frameWidth);
  const BC = offsetLine(B, C, frameWidth);
  const CA = offsetLine(C, A, frameWidth);

  //
  // 5. Intersect offset edges to get inner triangle
  //
  const A2 = intersect(AB, CA);
  const B2 = intersect(AB, BC);
  const C2 = intersect(BC, CA);

  innerPoints = [A2, B2, C2];
}
else { // orientation === 'left'
  // Left triangle: vertical right edge, horizontal bottom edge,
  // diagonal hypotenuse from top-right to bottom-left
  const A = [currentX + w + frameWidth, y + h + frameWidth];   // bottom-right
  const B = [currentX + w + frameWidth, y - frameWidth];       // top-right
  const C = [currentX - frameWidth,       y + h + frameWidth]; // bottom-left

  outerPoints = [A, B, C];

  function offsetLine(P, Q, d) {
    const dx = Q[0] - P[0];
    const dy = Q[1] - P[1];
    const len = Math.hypot(dx, dy);

    // inward normal (for right orientation)
    const nx = -dy / len;
    const ny = dx / len;

    return [
      [P[0] + nx * d, P[1] + ny * d],
      [Q[0] + nx * d, Q[1] + ny * d]
    ];
  }

  function intersect(L1, L2) {
    const [A, B] = L1;
    const [C, D] = L2;

    const a1 = B[1] - A[1];
    const b1 = A[0] - B[0];
    const c1 = a1 * A[0] + b1 * A[1];

    const a2 = D[1] - C[1];
    const b2 = C[0] - D[0];
    const c2 = a2 * C[0] + b2 * C[1];

    const det = a1 * b2 - a2 * b1;

    return [
      (b2 * c1 - b1 * c2) / det,
      (a1 * c2 - a2 * c1) / det
    ];
  }

  // NOTE: use -frameWidth here to flip direction (inward for left)
  const AB = offsetLine(A, B, -frameWidth);
  const BC = offsetLine(B, C, -frameWidth);
  const CA = offsetLine(C, A, -frameWidth);

  const A2 = intersect(AB, CA); // inner bottom-right
  const B2 = intersect(AB, BC); // inner top-right
  const C2 = intersect(BC, CA); // inner bottom-left

  innerPoints = [A2, B2, C2];
}

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      const outerPointsStr = outerPoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', outerPointsStr)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === BASE FILL ===
    const innerPointsStr = innerPoints.map(p => p.join(',')).join(' ');
    svg.append('polygon')
      .attr('points', innerPointsStr)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY ===
    if (tintOverlay) {
      svg.append('polygon')
        .attr('points', innerPointsStr)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === SUBFRAME (middle frame between outer frame and glass) ===
    if (drawFrame && ((subFrame === true && index === 0) || (forceSubFrame === true)) && middleFrame) {
      const subInset = frameWidth / 2; // distance from outer frame to middle frame

      // Calculate middle frame triangle points by offsetting from outer points
      let middlePoints;

      if (orientation === 'center') {
        const [A, B, C] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CA = offsetLine(C, A, subInset);

        const A2 = intersect(AB, CA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CA);

        middlePoints = [A2, B2, C2];
      } else if (orientation === 'right') {
        const [A, B, C] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, subInset);
        const BC = offsetLine(B, C, subInset);
        const CA = offsetLine(C, A, subInset);

        const A2 = intersect(AB, CA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CA);

        middlePoints = [A2, B2, C2];
      } else { // left
        const [A, B, C] = outerPoints;

        function offsetLine(P, Q, d) {
          const dx = Q[0] - P[0];
          const dy = Q[1] - P[1];
          const len = Math.hypot(dx, dy);
          const nx = -dy / len;
          const ny = dx / len;
          return [
            [P[0] + nx * d, P[1] + ny * d],
            [Q[0] + nx * d, Q[1] + ny * d]
          ];
        }

        function intersect(L1, L2) {
          const [A, B] = L1;
          const [C, D] = L2;
          const a1 = B[1] - A[1];
          const b1 = A[0] - B[0];
          const c1 = a1 * A[0] + b1 * A[1];
          const a2 = D[1] - C[1];
          const b2 = C[0] - D[0];
          const c2 = a2 * C[0] + b2 * C[1];
          const det = a1 * b2 - a2 * b1;
          return [
            (b2 * c1 - b1 * c2) / det,
            (a1 * c2 - a2 * c1) / det
          ];
        }

        const AB = offsetLine(A, B, -subInset);
        const BC = offsetLine(B, C, -subInset);
        const CA = offsetLine(C, A, -subInset);

        const A2 = intersect(AB, CA);
        const B2 = intersect(AB, BC);
        const C2 = intersect(BC, CA);

        middlePoints = [A2, B2, C2];
      }

      const middlePointsStr = middlePoints.map(p => p.join(',')).join(' ');
      svg.append('polygon')
        .attr('points', middlePointsStr)
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel) {
      // Position label at centroid of triangle
      let centroidX, centroidY;

      if (orientation === 'center') {
        // For center triangle (isosceles pointing up), centroid is at w/2 horizontally
        centroidX = currentX + w / 2;
        centroidY = y + (2 * h) / 3;  // Centroid is 2/3 down from apex
      } else if (orientation === 'right') {
        centroidX = currentX + w / 3;
        centroidY = y + h / 3;
      } else {
        centroidX = currentX + (2 * w) / 3;
        centroidY = y + h / 3;
      }

      svg.append('text')
        .attr('x', centroidX)
        .attr('y', centroidY + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    currentX += w + 2 * frameWidth + d + 2 * lineFrameThickness;

    prev = {
      w, h, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      orientation, fullCorners, halfCorners, displayLabel,
      glassType, glassDesign, middleFrame
    };
  });

  return innerPoints;
}
